import React, { useEffect, useState } from "react";
import { Building2, User as UserIcon, Plus, Users, Crown, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { v2Api } from "@/lib/v2Api";
import { confirm } from "@/lib/confirm";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const ROLE_META = {
  lead:   { icon: Crown,   color: "text-amber-600", chip: "bg-amber-50 text-amber-700 border-amber-200" },
  member: { icon: UserIcon, color: "text-blue-600",  chip: "bg-blue-50 text-blue-700 border-blue-200"  },
};

function MembersPanel({ deptId, allUsers, isAdmin = false, userId = null }) {
  const [members, setMembers] = useState([]);
  const [draftUserId, setDraftUserId] = useState("");
  const [draftRole, setDraftRole] = useState("member");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try { setMembers(await v2Api.departmentMembers.list(deptId) || []); }
    catch { /* non-fatal */ }
  };
  useEffect(() => { if (deptId) refresh(); /* eslint-disable-next-line */ }, [deptId]);

  // Derive permissions from actual membership data — more reliable than cross-endpoint
  // comparison, and works as soon as members load.
  const isDeptLead = members.some((m) => m.user_id === userId && m.role === "lead" && m.is_active !== false);
  const canManage = isAdmin || isDeptLead;

  const add = async () => {
    if (!draftUserId) return;
    setBusy(true);
    try {
      await v2Api.departmentMembers.add(deptId, {
        user_id: draftUserId, role: draftRole,
      });
      setDraftUserId("");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Add failed");
    } finally { setBusy(false); }
  };

  const updateRole = async (m, role) => {
    try {
      await v2Api.departmentMembers.patch(m.id, { role });
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Update failed");
    }
  };

  const remove = async (m) => {
    if (!await confirm(`Remove ${m.user_name || m.user_email}?`, { title: "Remove member", confirmLabel: "Remove" })) return;
    try {
      await v2Api.departmentMembers.remove(m.id);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Remove failed");
    }
  };

  const memberIds = new Set(members.map((m) => m.user_id));
  const candidates = allUsers.filter((u) => !memberIds.has(u.id));

  return (
    <div className="pt-2 border-t space-y-2">
      <p className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1">
        <Users className="w-3 h-3" /> Members ({members.length})
      </p>
      <div className="space-y-1">
        {members.map((m) => {
          const meta = ROLE_META[m.role] || ROLE_META.member;
          const Icon = meta.icon;
          return (
            <div key={m.id} className="flex items-center justify-between gap-2 text-xs">
              <div className="flex items-center gap-1.5 min-w-0">
                <Icon className={`w-3.5 h-3.5 shrink-0 ${meta.color}`} />
                <span className="truncate text-slate-700">
                  {m.user_name || m.user_email}
                </span>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {canManage && (isAdmin || m.role !== "lead") ? (
                  <>
                    <select
                      value={m.role}
                      onChange={(e) => updateRole(m, e.target.value)}
                      className={`text-[10px] px-1.5 py-0.5 rounded-full border ${meta.chip}`}
                    >
                      {isAdmin && <option value="lead">lead</option>}
                      <option value="member">member</option>
                    </select>
                    <button onClick={() => remove(m)} className="text-slate-300 hover:text-rose-500" title="Remove">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </>
                ) : (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${meta.chip}`}>
                    {m.role}
                  </span>
                )}
              </div>
            </div>
          );
        })}
        {members.length === 0 && (
          <p className="text-[10px] italic text-slate-400">No members yet.</p>
        )}
      </div>
      {canManage && candidates.length > 0 && (
        <div className="flex items-center gap-1 pt-1">
          <select
            value={draftUserId}
            onChange={(e) => setDraftUserId(e.target.value)}
            className="flex-1 text-xs px-2 py-1 border rounded-md bg-white"
          >
            <option value="">+ add user…</option>
            {candidates.map((u) => (
              <option key={u.id} value={u.id}>{u.full_name || u.name || u.email}</option>
            ))}
          </select>
          <select
            value={draftRole}
            onChange={(e) => setDraftRole(e.target.value)}
            className="text-xs px-2 py-1 border rounded-md bg-white"
          >
            {isAdmin && <option value="lead">lead</option>}
            <option value="member">member</option>
          </select>
          <Button size="sm" onClick={add} disabled={busy || !draftUserId}>Add</Button>
        </div>
      )}
    </div>
  );
}


function AddDepartmentForm({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const reset = () => { setCode(""); setName(""); };
  const submit = async () => {
    if (!code.trim() || !name.trim()) {
      toast.error("Code and name are required"); return;
    }
    setBusy(true);
    try {
      const dept = await v2Api.createDepartment({
        code: code.trim().toUpperCase(),
        name: name.trim(),
      });
      toast.success(`Department ${dept.code} created`);
      reset(); setOpen(false); onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Create failed");
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <Plus className="w-4 h-4 mr-1" /> Add department
      </Button>
    );
  }
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2 max-w-md">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] uppercase tracking-wide text-slate-500 block mb-0.5">Code</label>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="ELEC"
            disabled={busy}
            className="w-full text-sm px-2 py-1 border border-slate-300 rounded outline-none focus:border-blue-400 font-mono"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wide text-slate-500 block mb-0.5">Name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Electrical"
            disabled={busy}
            className="w-full text-sm px-2 py-1 border border-slate-300 rounded outline-none focus:border-blue-400"
          />
        </div>
      </div>
      <div className="flex justify-end gap-1.5">
        <button onClick={() => { setOpen(false); reset(); }} disabled={busy}
                className="text-xs px-2 py-1 rounded text-slate-600 hover:bg-slate-200">
          Cancel
        </button>
        <button onClick={submit} disabled={busy}
                className="text-xs px-3 py-1 rounded bg-blue-600 text-white disabled:opacity-50 hover:bg-blue-700">
          {busy ? "Creating…" : "Create"}
        </button>
      </div>
    </div>
  );
}


export default function DepartmentsPage() {
  const { user } = useAuth();
  const [depts, setDepts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    Promise.allSettled([
      v2Api.departments(),
      v2Api.departmentLeads(),
      v2Api.workspaceMembers(),
    ]).then(([d, l, u]) => {
      if (d.status === "fulfilled") setDepts(d.value || []);
      if (l.status === "fulfilled") setLeads(l.value || []);
      if (u.status === "fulfilled") setAllUsers(u.value || []);
      setLoading(false);
    });
  };
  useEffect(() => { refresh(); }, []);

  const isAdmin = user?.role === "admin" || user?.role === "manager";
  const leadByDept = Object.fromEntries(leads.map((l) => [l.department_code, l]));

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-600" /> Departments
          </h1>
          <p className="text-sm text-slate-500">
            Workspace departments, their in-charges, and members. Section classification
            keywords are managed in Settings → Section registry.
          </p>
        </div>
        {isAdmin && <AddDepartmentForm onCreated={refresh} />}
      </div>

      {loading && <p className="text-sm text-slate-500">Loading…</p>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {depts.map((d) => {
          const lead = leadByDept[d.code];
          return (
            <Card key={d.id}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{d.name}</CardTitle>
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">{d.code}</p>
              </CardHeader>
              <CardContent className="text-sm space-y-3">
                <div className="flex items-center gap-2 text-xs">
                  <UserIcon className="w-3.5 h-3.5 text-slate-400" />
                  {lead?.lead_user_name ? (
                    <span className="text-slate-700">{lead.lead_user_name}</span>
                  ) : (
                    <span className="italic text-slate-400">In-charge unassigned</span>
                  )}
                </div>
                <MembersPanel
                  deptId={d.id}
                  allUsers={allUsers}
                  isAdmin={isAdmin}
                  userId={user?.id}
                />
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
